import React, { useState, useEffect } from 'react';
import { Calendar, Plus, ChevronLeft, ChevronRight, Search, Filter, RefreshCw, Clock, X, Check } from 'lucide-react';
import AppointmentForm from './AppointmentForm';
import { API_URL } from '../config/api';
import './AppointmentsManager.css';

const toYMD = (d) => {
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
};

const AppointmentsManager = () => {
    const [doctors, setDoctors] = useState([]);
    const [selectedDate, setSelectedDate] = useState(toYMD(new Date()));
    const [filters, setFilters] = useState({
        view: 'day', // day, all
        doctor_id: '',
        branch: '',
        status: '',
        search: ''
    });
    const [showNewAppointment, setShowNewAppointment] = useState(false);
    const [editingStatus, setEditingStatus] = useState(null);



    useEffect(() => {
        fetchAppointments();
        fetchDoctors();
    }, [selectedDate, filters.view]);

    const fetchAppointments = async () => {
        try {
            setLoading(true);
            const token = localStorage.getItem('token');

            let url = `${API_URL}/appointments`;
            const params = new URLSearchParams();

            if (filters.view === 'day') {
                params.append('date_from', selectedDate);
                params.append('date_to', selectedDate);
            }
            if (filters.doctor_id) params.append('doctor_id', filters.doctor_id);
            if (filters.branch) params.append('branch', filters.branch);
            if (filters.status) params.append('status', filters.status);

            if (params.toString()) {
                url += '?' + params.toString();
            }

            const response = await fetch(url, {
                headers: { 'Authorization': `Bearer ${token}` }
            });

            if (response.ok) {
                let data = await response.json();

                // Apply search filter locally
                if (filters.search) {
                    const searchLower = filters.search.toLowerCase();
                    data = data.filter(apt =>
                        apt.patient_name?.toLowerCase().includes(searchLower) ||
                        apt.doctor_name?.toLowerCase().includes(searchLower) ||
                        apt.patient_phone?.includes(filters.search)
                    );
                }

                // Sort by time
                data.sort((a, b) => {
                    if (a.appointment_date !== b.appointment_date) {
                        return a.appointment_date.localeCompare(b.appointment_date);
                    }
                    return (a.start_time || '').localeCompare(b.start_time || '');
                });

                setAppointments(data);
            }
        } catch (err) {
            console.error('Error fetching appointments:', err);
        } finally {
            setLoading(false);
            console.error('Error fetching doctors:', err);
        }
    };

    const updateStatus = async (id, newStatus) => {
        try {
            const token = localStorage.getItem('token');
            const response = await fetch(`${API_URL}/appointments/${id}`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({ status: newStatus })
            });

            if (response.ok) {
                fetchAppointments();
                setEditingStatus(null);
            } else {
                const data = await response.json();
                alert(data.message || 'Error al actualizar');
            }
        } catch (err) {
            alert('Error de conexión');
        }
    };

    const cancelAppointment = async (id) => {
        if (!confirm('¿Está seguro de cancelar esta cita?')) return;

        try {
            const token = localStorage.getItem('token');
            const response = await fetch(`${API_URL}/appointments/${id}`, {
                method: 'DELETE',
                headers: { 'Authorization': `Bearer ${token}` }
            });

            if (response.ok) {
                fetchAppointments();
            } else {
                const data = await response.json();
                alert(data.message || 'Error al cancelar');
            }
        } catch (err) {
            alert('Error de conexión');
        }
    };

    const goToDate = (direction) => {
        const date = new Date(selectedDate);
        date.setDate(date.getDate() + direction);
        setSelectedDate(toYMD(date));
    };

    const goToToday = () => {
        setSelectedDate(toYMD(new Date()));
    };

    const formatDate = (dateStr) => {
        const date = new Date(dateStr + 'T00:00:00');
        return date.toLocaleDateString('es-ES', {
            weekday: 'long',
            day: 'numeric',
            month: 'long',
            year: 'numeric'
        });
    };

    const formatTime = (time) => {
        if (!time) return '-';
        return time.substring(0, 5);
    };

    const getStatusInfo = (status) => {
        const map = {
            'pending': { label: 'Por confirmar', color: '#f59e0b', bg: '#fef3c7' },
            'confirmed': { label: 'Confirmada', color: '#00BBBF', bg: '#d1fae5' },
            'completed': { label: 'Completada', color: '#083677', bg: '#dbeafe' },
            'cancelled': { label: 'Cancelada', color: '#ef4444', bg: '#fee2e2' }
        };
        return map[status] || { label: status, color: '#6b7280', bg: '#f3f4f6' };
    };

    const handleFilterChange = (e) => {
        const { name, value } = e.target;
        setFilters(prev => ({ ...prev, [name]: value }));
    };

    const applyFilters = () => {
        fetchAppointments();
    };

    return (
        <div className="appointments-manager">
            {/* Header */}
            <div className="manager-header">
                <div className="header-title">
                    <Calendar size={28} />
                    <div>
                        <h1>Gestión de Citas</h1>
                        <p>Administra y actualiza el estado de las citas</p>
                    </div>
                </div>
                <button className="btn-new-apt" onClick={() => setShowNewAppointment(true)}>
                    <Plus size={20} />
                    Nueva Cita
                </button>
            </div>

            {/* Date Navigation */}
            <div className="date-navigation">
                <div className="date-controls">
                    <button onClick={() => goToDate(-1)} className="btn-nav">
                        <ChevronLeft size={20} />
                        Día Anterior
                    </button>
                    <button onClick={goToToday} className="btn-today">
                        Hoy
                    </button>
                    <button onClick={() => goToDate(1)} className="btn-nav">
                        Día Siguiente
                        <ChevronRight size={20} />
                    </button>
                    <div className="date-picker">
                        <span>Fecha:</span>
                        <input
                            type="date"
                            value={selectedDate}
                            onChange={(e) => setSelectedDate(e.target.value)}
                        />
                    </div>
                </div>
                <div className="current-date">
                    Citas del Día ? {formatDate(selectedDate)}
                </div>
            </div>

            {/* Filters */}
            <div className="filters-bar">
                <div className="filter-group-inline">
                    <label>Listado:</label>
                    <div className="btn-group">
                        <button
                            className={`btn-toggle ${filters.view === 'day' ? 'active' : ''}`}
                            onClick={() => setFilters(prev => ({ ...prev, view: 'day' }))}
                        >
                            Día
                        </button>
                        <button
                            className={`btn-toggle ${filters.view === 'all' ? 'active' : ''}`}
                            onClick={() => setFilters(prev => ({ ...prev, view: 'all' }))}
                        >
                            Todas
                        </button>
                    </div>
                </div>

                <div className="filter-group-inline">
                    <label>Doctor:</label>
                    <select name="doctor_id" value={filters.doctor_id} onChange={handleFilterChange}>
                        <option value="">Todos los Doctores</option>
                        {doctors.map(doc => (
                            <option key={doc.id} value={doc.id}>{doc.name}</option>
                        ))}
                    </select>
                </div>

                <div className="filter-group-inline">
                    <label>Sucursal:</label>
                    <select name="branch" value={filters.branch} onChange={handleFilterChange}>
                        <option value="">Todas</option>
                        <option value="GENETICA">GENETICA</option>
                        <option value="MATRIZ">MATRIZ</option>
                        <option value="NORTE">NORTE</option>
                    </select>
                </div>

                <div className="filter-group-inline">
                    <label>Estatus:</label>
                    <select name="status" value={filters.status} onChange={handleFilterChange}>
                        <option value="">Todos</option>
                        <option value="pending">Por confirmar</option>
                        <option value="confirmed">Confirmada</option>
                        <option value="completed">Completada</option>
                        <option value="cancelled">Cancelada</option>
                    </select>
                </div>

                <div className="filter-search">
                    <input
                        type="text"
                        placeholder="Buscar paciente..."
                        name="search"
                        value={filters.search}
                        onChange={handleFilterChange}
                    />
                </div>

                <button className="btn-apply-filters" onClick={applyFilters}>
                    <Filter size={18} />
                    Filtrar
                </button>

                <button className="btn-refresh" onClick={fetchAppointments}>
                    <RefreshCw size={18} />
                </button>
            </div>

            {/* Results Count */}
            <div className="results-info">
                <span>Mostrando <strong>{appointments.length}</strong> citas</span>
            </div>

            {/* Table */}
            <div className="table-container">
                {loading ? (
                    <div className="loading-state">
                        <div className="spinner"></div>
                        <p>Cargando citas...</p>
                    </div>
                ) : appointments.length === 0 ? (
                    <div className="empty-state">
                        <Calendar size={48} />
                        <h3>No hay citas</h3>
                        <p>No se encontraron citas para los filtros seleccionados</p>
                    </div>
                ) : (
                    <table className="appointments-table">
                        <thead>
                            <tr>
                                <th>Hora</th>
                                <th>Paciente</th>
                                <th>Teléfono</th>
                                <th>Doctor</th>
                                <th>Consultorio</th>
                                <th>Sucursal</th>
                                <th>Motivo</th>
                                <th>Estatus</th>
                                <th>Acciones</th>
                            </tr>
                        </thead>
                        <tbody>
                            {appointments.map(apt => {
                                const statusInfo = getStatusInfo(apt.status);
                                return (
                                    <tr key={apt.id} className={apt.status === 'cancelled' ? 'cancelled-row' : ''}>
                                        <td className="time-cell">
                                            <Clock size={14} />
                                            <span>{formatTime(apt.start_time)}</span>
                                            {apt.end_time && <span className="time-end">- {formatTime(apt.end_time)}</span>}
                                        </td>
                                        <td className="patient-cell">
                                            <strong>{apt.patient_name || 'N/A'}</strong>
                                            {filters.view === 'all' && (
                                                <span className="date-badge">{apt.appointment_date}</span>
                                            )}
                                        </td>
                                        <td>{apt.patient_phone || '-'}</td>
                                        <td>{apt.doctor_name || '-'}</td>
                                        <td>{apt.office || '-'}</td>
                                        <td>{apt.branch || '-'}</td>
                                        <td className="reason-cell">{apt.reason || '-'}</td>
                                        <td>
                                            {editingStatus === apt.id ? (
                                                <div className="status-edit">
                                                    <select
                                                        defaultValue={apt.status}
                                                        onChange={(e) => updateStatus(apt.id, e.target.value)}
                                                    >
                                                        <option value="pending">Por confirmar</option>
                                                        <option value="confirmed">Confirmada</option>
                                                        <option value="completed">Completada</option>
                                                    </select>
                                                    <button className="btn-cancel-edit" onClick={() => setEditingStatus(null)}>
                                                        <X size={14} />
                                                    </button>
                                                </div>
                                            ) : (
                                                <span
                                                    className="status-badge clickable"
                                                    style={{ backgroundColor: statusInfo.bg, color: statusInfo.color }}
                                                    onClick={() => apt.status !== 'cancelled' && setEditingStatus(apt.id)}
                                                    title={apt.status !== 'cancelled' ? 'Clic para cambiar estatus' : ''}
                                                >
                                                    {statusInfo.label}
                                                </span>
                                            )}
                                        </td>
                                        <td className="actions-cell">
                                            {apt.status !== 'cancelled' && (
                                                <>
                                                    {apt.status === 'pending' && (
                                                        <button
                                                            className="btn-action confirm"
                                                            onClick={() => updateStatus(apt.id, 'confirmed')}
                                                            title="Confirmar"
                                                        >
                                                            <Check size={16} />
                                                        </button>
                                                    )}
                                                    <button
                                                        className="btn-action cancel"
                                                        onClick={() => cancelAppointment(apt.id)}
                                                        title="Cancelar"
                                                    >
                                                        <X size={16} />
                                                    </button>
                                                </>
                                            )}
                                            {apt.status === 'cancelled' && (
                                                <span className="cancelled-label">Cancelada</span>
                                            )}
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                )}
            </div>

            {/* New Appointment Modal */}
            {showNewAppointment && (
                <AppointmentForm
                    onSuccess={() => {
                        setShowNewAppointment(false);
                        fetchAppointments();
                    }}
                    onCancel={() => setShowNewAppointment(false)}
                />
            )}
        </div>
    );
};

export default AppointmentsManager;
